import { ActionIcon, Select, SelectItemProps, Text, Flex } from "@mantine/core";
import { IconCircleFilled, IconSearch } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useCallback, useMemo, useRef, useState, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetApi } from "src/hooks";
import { getYachtId } from "src/utils";
import { SelectItem } from "../EntrySelectItem/EntrySelectItem.styled";

const SearchSelectItem = forwardRef<
  HTMLDivElement,
  SelectItemProps & { status: number; mention?: string; showMention?: boolean }
>(({ label, value, status, mention, showMention, ...others }, ref) => {
  const statusMap = {
    0: { label: "Draft", color: "red.9" },
    1: { label: "Open", color: "orange.4" },
    2: { label: "Re-Verify", color: "orange.4" },
    3: { label: "Yacht Verified", color: "green.8" },
  };
  return (
    <SelectItem
      ref={ref}
      style={{ display: "flex", alignItems: "center" }}
      {...others}>
      <Flex direction="column">
        {label}
        {showMention && mention && (
          <Text size="xs" ml={5}>
            {mention}
          </Text>
        )}
      </Flex>
      <ActionIcon color={statusMap[status as keyof typeof statusMap]?.color}>
        <IconCircleFilled size={12} />
      </ActionIcon>
    </SelectItem>
  );
});

interface SearchEntry {
  lbeid?: string;
  meid?: string;
  partid?: string;
  enum: number;
  datecreated: string;
  entrydate: string;
  status: number;
  [key: string]: any;
}

const SearchRefInput = () => {
  const navigate = useNavigate();
  const { logbookId, idType } = useParams();
  const [refSearch, setRefSearch] = useState("");
  const [searchResults, setSearchResults] = useState<SearchEntry[]>([]);
  const [isUserSearch, setIsUserSearch] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const yachtid = getYachtId();

  const { refetch } = useGetApi({
    request: "search-entry-ref",
    body: {},
    fetchOnInit: false,
  });
  const performSearch = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        setIsUserSearch(false);
        return;
      }

      try {
        const data = (await refetch({
          request: "search-entry-ref",
          params: {
            yachtid,
            ref: searchTerm.trim(),
            ...(logbookId && { lbid: logbookId }),
          },
        })) as { list: SearchEntry[]; isUserSearch?: boolean };

        if (data?.list) {
          setSearchResults(data.list);
          setIsUserSearch(!!data.isUserSearch);
        }
      } catch (error: any) {
        setSearchResults([]);
        setIsUserSearch(false);
      }
    },
    [yachtid, logbookId, refetch],
  );

  const handleSearchRef = useCallback(
    (searchTerm: string) => {
      setRefSearch(searchTerm);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (!searchTerm.trim()) {
        setSearchResults([]);
        setIsUserSearch(false);
        return;
      }

      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, 300);
    },
    [performSearch],
  );

  const selectData = useMemo(
    () =>
      searchResults
        .filter(s => s.status !== 99)
        .map((entry: SearchEntry) => {
          const entryId =
            entry[
              idType === "meid"
                ? "meid"
                : idType === "partid"
                  ? "partid"
                  : "lbeid"
            ];
          const date = entry.datecreated || "";
          const formattedDate = date ? dayjs(date).format("D MMM YYYY") : "";

          return {
            value: entryId ?? "",
            label: `#${entry.enum ?? ""} - ${formattedDate}`,
            status: entry.status,
            mention: entry.mention ?? "",
          };
        }),
    [searchResults, idType],
  );

  const handleSelectChange = useCallback(
    (value: string | null) => {
      if (value) {
        setRefSearch("");
        setSearchResults([]);
        setIsUserSearch(false);
        navigate(`/logbook/${logbookId}/${idType}/${value}`, {
          replace: true,
        });
      }
    },
    [logbookId, idType, navigate],
  );

  return (
    <Select
      w={220}
      placeholder="Search Ref # / Name"
      icon={<IconSearch size={16} />}
      searchValue={refSearch}
      onSearchChange={handleSearchRef}
      onChange={handleSelectChange}
      filter={() => true}
      limit={undefined}
      data={selectData}
      itemComponent={v => (
        <SearchSelectItem showMention={isUserSearch} {...v} />
      )}
      value={null}
      searchable
      clearable
    />
  );
};

export default SearchRefInput;
